import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  X, DollarSign, Package, FileText, Settings, Shield,
  Pin, Calendar, CalendarDays, Folder, Sparkles, ClipboardList, Inbox, Heart, HandHeart, AlertTriangle,
} from 'lucide-react'
import { useRole } from '../../hooks/useRole'
import { useModules } from '../../hooks/useModules'
import { useOrg } from '../../hooks/useOrg'
import { supabase } from '../../lib/supabase'

const ALL_ITEMS = [
  { to: '/finances',   icon: DollarSign, label: 'Finances',   module: 'finances',   roles: ['admin','member','super_admin'] },
  { to: '/inventory',  icon: Package,    label: 'Inventory',  module: 'inventory',  roles: ['admin','member','super_admin'] },
  { to: '/agreements', icon: FileText,   label: 'Agreements', module: 'agreements', roles: ['admin','member','foster','super_admin'] },
  { to: '/applications', icon: ClipboardList, label: 'Applications', module: null,  roles: ['admin','member','super_admin'] },
  { to: '/inbox',      icon: Inbox,      label: 'Inbox',      module: null,         roles: ['admin','member','super_admin'] },
  { to: '/board',      icon: Pin,        label: 'Board',      module: null,         roles: ['admin','member','foster','super_admin'] },
  { to: '/calendar',   icon: CalendarDays, label: 'Calendar', module: null,         roles: ['admin','member','super_admin'] },
  { to: '/documents',  icon: Folder,     label: 'Documents',  module: null,         roles: ['admin','member','foster','super_admin'] },
  { to: '/donors',     icon: Heart,      label: 'Donors',     module: 'donors',     roles: ['admin','super_admin'] },
  { to: '/adopters',   icon: HandHeart,  label: 'Adopters',   module: null,         roles: ['admin','member','super_admin'] },
  { to: '/support',    icon: Sparkles,   label: 'Support',    module: null,         roles: ['foster','admin','super_admin'] },
  { to: '/safety-alerts', icon: AlertTriangle, label: 'Safety Alerts', module: null, roles: ['admin','super_admin'], badge: 'safety' },
  { to: '/settings',   icon: Settings,   label: 'Settings',   module: null,         roles: ['admin','member','foster','super_admin'] },
  { to: '/platform',   icon: Shield,     label: 'Platform Admin', module: null,     roles: ['super_admin'] },
]

export default function MoreMenu({ isOpen, onClose }) {
  const navigate = useNavigate()
  const { role, isAdmin } = useRole()
  const { isEnabled } = useModules()
  const { orgId } = useOrg()
  const [safetyCount, setSafetyCount] = useState(0)

  useEffect(() => {
    if (!orgId || !isAdmin || !isOpen) return
    supabase
      .from('safety_alerts')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('status', 'new')
      .then(({ count }) => setSafetyCount(count || 0))
  }, [orgId, isAdmin, isOpen])

  if (!isOpen) return null

  function handleNavigate(to) {
    navigate(to)
    onClose()
  }

  const visibleItems = ALL_ITEMS.filter(item =>
    item.roles.includes(role) && (!item.module || isEnabled(item.module))
  )

  return (
    <div className="fixed inset-0 z-50 animate-fade-in">
      <div className="absolute inset-0 bg-on-surface/25" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 frosted-glass rounded-t-2xl shadow-float safe-bottom animate-slide-up">
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <h2 className="text-base font-semibold tracking-[0.05em] text-on-surface">More</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-surface-container-high text-on-surface-variant"
            aria-label="Close menu"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>
        <div className="px-3 pb-5 space-y-0.5 max-h-[60vh] overflow-y-auto">
          {visibleItems.map(item => (
            <button
              key={item.to}
              onClick={() => handleNavigate(item.to)}
              className="w-full flex items-center gap-4 px-4 py-3.5 rounded-md text-on-surface hover:bg-surface-container-low transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-full bg-surface-container-high flex items-center justify-center">
                <item.icon size={18} strokeWidth={1.5} className="text-on-surface-variant" />
              </div>
              <span className="text-sm font-medium flex-1">{item.label}</span>
              {item.badge === 'safety' && safetyCount > 0 && (
                <span
                  className="min-w-[18px] h-[18px] rounded-full text-[0.5625rem] font-bold flex items-center justify-center flex-shrink-0 px-1.5"
                  style={{ background: '#ba1a1a', color: '#ffffff' }}
                >
                  {safetyCount > 99 ? '99+' : safetyCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
